// Verify the short-lived EdDSA JWT included in the WS hello message.
//
// The auth-server mints these via POST /auth/ws-token using its Ed25519
// signing key. The daemon pinned the matching public key at link time
// (serverPubkeyHex in persisted-config.ts). Verification is local —
// no DB round-trip, no allow-list lookup.
//
// Format: standard compact JWT with base64url parts.
//   header.payload.signature
//   header:  { alg: "EdDSA", kid: "v1" }
//   payload: { sub, device, iat, exp, kid }
//
// Security properties:
//   - Signature binds token to this auth-server (prevents forgery).
//   - `exp` enforces 5-min TTL (revocation latency window).
//   - `device` binds token to the E2E peer pubkey (prevents relay theft).
//   - 30-second `exp` leeway tolerates minor clock skew.

import { createPublicKey, verify as edVerify } from "node:crypto";

export type WsJwtResult =
  | { ok: true; userId: string; scope?: string }
  | { ok: false; reason: string };

/**
 * Decode a base64url-encoded segment of a JWT (no padding variant of base64).
 * Node.js Buffer supports "base64url" natively since v6.
 */
function decodeSegment(segment: string): unknown {
  return JSON.parse(Buffer.from(segment, "base64url").toString("utf8"));
}

/**
 * Verify the WS-hello JWT and return either the authenticated userId or a
 * rejection reason. Always returns a plain object — never throws.
 *
 * @param token           Raw JWT string from the WS hello message.
 * @param serverPubkeyHex Pinned Ed25519 public key (hex) from the daemon's
 *                        persisted config (account.serverPubkey).
 * @param peerPublicKeyB64 Base64 X25519 pubkey of the E2E peer, captured from
 *                        the relay handshake. If null (direct connections with
 *                        no E2E), the device binding check is skipped.
 * @param ownServerId     The daemon's own stable serverId. When provided and the
 *                        JWT contains a `host` claim, the claim must match.
 *                        If the daemon doesn't know its own serverId yet (e.g.
 *                        unlinked state), pass null to skip the check.
 */
export function verifyWsJwt(
  token: string,
  serverPubkeyHex: string,
  peerPublicKeyB64: string | null,
  ownServerId?: string | null,
): WsJwtResult {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return { ok: false, reason: "malformed token" };
    const [headerSeg, payloadSeg, sigSeg] = parts;

    // Decode header — check kid before touching crypto.
    const header = decodeSegment(headerSeg) as { alg?: string; kid?: string };
    if (header.kid !== "v1") {
      return { ok: false, reason: `unknown kid: ${header.kid}` };
    }

    // Decode payload.
    const payload = decodeSegment(payloadSeg) as {
      sub?: string;
      device?: string;
      iat?: number;
      exp?: number;
      host?: string;
      scope?: string;
    };

    // Verify Ed25519 signature over `header.payload`.
    const signingInput = `${headerSeg}.${payloadSeg}`;
    const sigBytes = Buffer.from(sigSeg, "base64url");
    const pubBytes = Buffer.from(serverPubkeyHex, "hex");
    if (pubBytes.length !== 32) return { ok: false, reason: "invalid pubkey length" };
    const spkiPrefix = Buffer.from("302a300506032b6570032100", "hex");
    const spki = Buffer.concat([spkiPrefix, pubBytes]);
    const pubKey = createPublicKey({ key: spki, format: "der", type: "spki" });

    const signatureValid = edVerify(null, Buffer.from(signingInput, "utf8"), pubKey, sigBytes);
    if (!signatureValid) return { ok: false, reason: "invalid signature" };

    // Check expiry with 30-second leeway for clock skew.
    const now = Math.floor(Date.now() / 1000);
    if (!payload.exp || payload.exp + 30 < now) {
      return { ok: false, reason: "token expired" };
    }

    // Device binding — skip for direct (non-relay) connections where there
    // is no E2E handshake and therefore no peer pubkey to compare against.
    if (peerPublicKeyB64 !== null) {
      if (payload.device !== peerPublicKeyB64) {
        return { ok: false, reason: "device mismatch" };
      }
    }

    if (!payload.sub) return { ok: false, reason: "missing sub" };

    // Host claim check (defence in depth). Only enforced when:
    //   - the JWT includes a `host` claim (minted with hostServerId), AND
    //   - the daemon knows its own serverId (ownServerId is a non-empty string)
    if (payload.host && ownServerId) {
      if (payload.host !== ownServerId) {
        return { ok: false, reason: "host mismatch" };
      }
    }

    return {
      ok: true,
      userId: payload.sub,
      ...(typeof payload.scope === "string" ? { scope: payload.scope } : {}),
    };
  } catch {
    return { ok: false, reason: "parse error" };
  }
}
